export const creativeAssetRoles = ["placeholder", "draft", "revision", "final"] as const;
export const creativeAssetSources = ["designer_upload", "fal_placeholder"] as const;

export type CreativeAssetRole = (typeof creativeAssetRoles)[number];
export type CreativeAssetSource = (typeof creativeAssetSources)[number];

export type CreativeAssetKeyInput = {
  contentPieceId: string;
  source: CreativeAssetSource;
  role: CreativeAssetRole;
  fileName: string;
  mimeType: string;
  byteSize: number;
  contentHash?: string;
  uploadedBy?: string;
  generatedByAgentRunId?: string;
};

const mimeExtensions: Record<string, string> = {
  "image/gif": "gif",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/svg+xml": "svg",
  "image/webp": "webp",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/webm": "webm",
};

export function buildCreativeAssetR2Key(input: CreativeAssetKeyInput): string {
  const contentPieceSegment = normalizeKeySegment(input.contentPieceId, "contentPieceId");
  const roleSegment = normalizeEnumSegment(input.role, creativeAssetRoles, "role");
  const sourceSegment = normalizeEnumSegment(input.source, creativeAssetSources, "source");
  const { stem, extension } = normalizeAssetFileName(input.fileName, input.mimeType);
  const fingerprint = getAssetFingerprint(input);

  return [
    "content-pieces",
    contentPieceSegment,
    "assets",
    roleSegment,
    sourceSegment,
    `${stem}-${fingerprint}.${extension}`,
  ].join("/");
}

/** Every creative asset key for a content piece starts with this prefix. */
export function buildContentPieceAssetKeyPrefix(contentPieceId: string): string {
  return `content-pieces/${normalizeKeySegment(contentPieceId, "contentPieceId")}/assets/`;
}

export function normalizeAssetFileName(fileName: string, mimeType: string): { stem: string; extension: string } {
  const baseName = getBaseFileName(fileName);
  if (!baseName) {
    throw new Error(
      `ODC_ASSET_001: Invalid asset file name. field=fileName observed=${JSON.stringify(fileName)} expected=non-empty file name. Next: choose a source file before creating an asset upload intent.`,
    );
  }

  const dotIndex = baseName.lastIndexOf(".");
  const rawStem = dotIndex > 0 ? baseName.slice(0, dotIndex) : baseName;
  const rawExtension = dotIndex > 0 ? baseName.slice(dotIndex + 1) : "";
  const stem = slugifyFileStem(rawStem) || "asset";
  const extension = normalizeExtension(rawExtension, mimeType);

  return { stem, extension };
}

function getBaseFileName(fileName: string): string {
  return fileName.trim().replace(/\\/g, "/").split("/").filter(Boolean).pop() ?? "";
}

function normalizeExtension(rawExtension: string, mimeType: string): string {
  const extension = rawExtension.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  if (extension.length > 0 && extension.length <= 12) {
    return extension;
  }

  return mimeExtensions[mimeType.trim().toLowerCase()] ?? "bin";
}

function getAssetFingerprint(input: CreativeAssetKeyInput): string {
  if (input.byteSize <= 0 || !Number.isFinite(input.byteSize)) {
    throw new Error(
      `ODC_ASSET_002: Invalid asset byte size. field=byteSize observed=${input.byteSize} expected=positive finite byte count. Next: read the file size before creating the asset upload intent.`,
    );
  }

  if (input.contentHash) {
    const normalizedHash = input.contentHash.trim().toLowerCase();
    if (!/^[a-f0-9]{16,128}$/.test(normalizedHash)) {
      throw new Error(
        `ODC_ASSET_003: Invalid asset content hash. field=contentHash observed=${JSON.stringify(input.contentHash)} expected=16-128 hex characters. Next: pass a lowercase or uppercase SHA-style hex digest, or omit contentHash for metadata fingerprinting.`,
      );
    }
    return normalizedHash.slice(0, 16);
  }

  return stableHexFingerprint([
    input.contentPieceId,
    input.source,
    input.role,
    input.fileName,
    input.mimeType,
    input.byteSize.toString(),
    input.uploadedBy ?? "",
    input.generatedByAgentRunId ?? "",
  ]);
}

function normalizeKeySegment(value: string, fieldPath: string): string {
  const segment = value.trim().replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "");
  if (!segment) {
    throw new Error(
      `ODC_ASSET_004: Invalid R2 key segment. field=${fieldPath} observed=${JSON.stringify(value)} expected=letters, numbers, dash, or underscore. Next: create the related Convex document before building an asset key.`,
    );
  }
  return segment;
}

function normalizeEnumSegment<T extends string>(value: string, allowed: readonly T[], fieldPath: string): string {
  if (!allowed.includes(value as T)) {
    throw new Error(
      `ODC_ASSET_005: Invalid asset enum value. field=${fieldPath} observed=${JSON.stringify(value)} expected=${allowed.join("|")}. Next: use the product asset contract values.`,
    );
  }
  return value.replace(/_/g, "-");
}

function slugifyFileStem(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function stableHexFingerprint(parts: string[]): string {
  const payload = parts.join("\u001f");
  return `${fnv1a32(payload)}${fnv1a32(`odc-assets\u001f${payload}`)}`;
}

function fnv1a32(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}
